import { nextTick, onMounted, onUnmounted } from 'vue'

/**
 * Composable that observes elements with the `.reveal` class and adds
 * a `.visible` class when they scroll into view (15 % threshold).
 *
 * CSS contract (add to the consuming component or App.vue):
 *   .reveal { opacity: 0; transform: translateY(32px); transition: opacity .7s ease, transform .7s ease; }
 *   .reveal.visible { opacity: 1; transform: none; }
 */
export function useScrollReveal() {
  let observer: IntersectionObserver | null = null
  let fallbackTimer: ReturnType<typeof setTimeout> | null = null

  function observe() {
    if (typeof IntersectionObserver === 'undefined') {
      // No support — show everything immediately
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('visible'))
      return
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer?.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.15 },
    )

    document.querySelectorAll('.reveal').forEach((el) => observer!.observe(el))

    // Safety fallback: reveal everything after 2 s in case observer never fires
    fallbackTimer = setTimeout(() => {
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('visible'))
    }, 2000)
  }

  onMounted(() => {
    nextTick(observe)
  })

  onUnmounted(() => {
    observer?.disconnect()
    if (fallbackTimer) clearTimeout(fallbackTimer)
  })
}
